/**
 * Frequently bought together
 * ---------------------------------------------------------------------------
 * Keeps the running total in step with the ticked rows and adds the whole
 * bundle in one request.
 *
 * The saving shown here is a preview of Shopify's automatic discount. This
 * file never applies a discount itself — the cart is the source of truth, so
 * the two can't drift apart at checkout.
 */

const ROOT = () => window.Shopify?.routes?.root || '/';

function money(cents) {
  const amount = cents / 100;
  const currency = window.Shopify?.currency?.active || 'INR';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
      style: 'currency',
      currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `₹${Math.round(amount)}`;
  }
}

class WbFbt extends HTMLElement {
  connectedCallback() {
    this.qualifyQty = Number(this.dataset.qualifyQty || 0);
    this.percent = Number(this.dataset.discountPercent || 0);

    this.checks = Array.from(this.querySelectorAll('[data-fbt-check]'));
    this.addBtn = this.querySelector('[data-fbt-add]');
    this.addLabel = this.querySelector('[data-fbt-add-label]');

    this.checks.forEach((check) => {
      check.addEventListener('change', () => {
        check.closest('.wb-fbt__row')?.classList.toggle('is-off', !check.checked);
        this.render();
      });
    });

    this.addBtn?.addEventListener('click', () => this.add());
    this.render();
  }

  selected() {
    return this.checks.filter((c) => c.checked);
  }

  render() {
    const chosen = this.selected();
    const count = chosen.length;
    const gross = chosen.reduce((sum, c) => sum + Number(c.dataset.price || 0), 0);

    const qualifies = this.qualifyQty > 0 && count >= this.qualifyQty && this.percent > 0;
    const net = qualifies ? Math.round(gross * (1 - this.percent / 100)) : gross;

    this.setText('[data-fbt-count]', String(count));
    this.setText('[data-fbt-total]', money(net));

    const was = this.querySelector('[data-fbt-was]');
    if (was) {
      was.textContent = qualifies ? money(gross) : '';
      was.hidden = !qualifies;
    }

    const saving = this.querySelector('[data-fbt-saving]');
    if (saving) {
      saving.textContent = qualifies ? `You save ${money(gross - net)}` : '';
      saving.hidden = !qualifies;
    }

    // Tell the shopper exactly how far off the offer they are, rather than
    // leaving them to work it out.
    const hint = this.querySelector('[data-fbt-hint]');
    if (hint) {
      const short = this.qualifyQty - count;
      hint.textContent =
        this.percent > 0 && short > 0
          ? `Add ${short} more ${short === 1 ? 'item' : 'items'} to save ${this.percent}%`
          : '';
    }

    if (this.addBtn) this.addBtn.disabled = count === 0;
  }

  setText(selector, value) {
    const el = this.querySelector(selector);
    if (el) el.textContent = value;
  }

  async add() {
    const items = this.selected()
      .map((c) => ({ id: Number(c.dataset.variant), quantity: 1 }))
      .filter((item) => item.id);

    if (!items.length) return;

    const original = this.addLabel?.textContent;
    if (this.addBtn) this.addBtn.disabled = true;
    if (this.addLabel) this.addLabel.textContent = 'Adding…';

    try {
      const response = await fetch(`${ROOT()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error('add failed');

      if (this.addLabel) this.addLabel.textContent = 'Added ✓';
      // Whichever cart drawer is installed, landing on the cart is the one
      // outcome that always shows the shopper what they just bought.
      window.location.href = `${ROOT()}cart`;
    } catch {
      if (this.addLabel) this.addLabel.textContent = 'Try again';
      if (this.addBtn) this.addBtn.disabled = false;
      setTimeout(() => {
        if (this.addLabel && original) this.addLabel.textContent = original;
      }, 1800);
    }
  }
}

if (!customElements.get('wb-fbt')) customElements.define('wb-fbt', WbFbt);
